import React, { useState, useEffect, useCallback } from 'react';
import { devError } from '../lib/devLog';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MarkdownContent from '../components/MarkdownContent';
import { Calendar, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [years, setYears] = useState([]);
  // Default to the latest year (2026 today) so the page opens with the most
  // current exhibition/news first — the Helen Masacz Anima announcement.
  // Resolved after `years` loads (see effect below); falls back to 'all' if
  // the endpoint returns nothing.
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch years
      const yearsRes = await fetch(`${API_URL}/api/news/years`);
      const yearsData = await yearsRes.json();
      setYears(yearsData);
      // On first load, pick the latest year automatically (data-driven so
      // this stays correct next year too).
      let year = selectedYear;
      if (year === null) {
        const latest = Array.isArray(yearsData) && yearsData.length > 0
          ? String(yearsData.sort((a, b) => b.year - a.year)[0].year)
          : 'all';
        year = latest;
        setSelectedYear(latest);
      }

      // Fetch news
      const newsUrl = year === 'all'
        ? `${API_URL}/api/news`
        : `${API_URL}/api/news?year=${year}`;
      
      const newsRes = await fetch(newsUrl);
      const newsData = await newsRes.json();
      setNews(newsData);
      
      setLoading(false);
    } catch (error) {
      devError('Error fetching news:', error);
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derive category counts across the (year-filtered) news set so counts
  // reflect the current year selection. Order tabs by frequency desc, with
  // "Exhibition" pinned first when it exists.
  const categoryCounts = news.reduce((acc, a) => {
    const c = (a.category || 'Uncategorised').trim() || 'Uncategorised';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const orderedCategories = Object.entries(categoryCounts)
    .sort(([a, ac], [b, bc]) => {
      if (a === 'Exhibition') return -1;
      if (b === 'Exhibition') return 1;
      return bc - ac;
    });

  // Client-side filter by category before grouping by year.
  const filteredNews = selectedCategory === 'all'
    ? news
    : news.filter((a) => (a.category || 'Uncategorised') === selectedCategory);

  // Group news by year, sort articles chronologically within each year
  // (NEWEST → oldest) so the latest story leads.
  const newsByYear = filteredNews.reduce((acc, article) => {
    const year = article.year || 'Unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(article);
    return acc;
  }, {});
  Object.keys(newsByYear).forEach((y) => {
    newsByYear[y].sort((a, b) => (b.iso_date || '').localeCompare(a.iso_date || ''));
  });

  // Latest year first (2026 before 2020).
  const sortedYears = Object.keys(newsByYear).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-sans text-sm">Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Editorial Hero — full-bleed Helmut Newton, ballerina & crocodile */}
      <section className="relative w-full h-[70vh] min-h-[520px] overflow-hidden">
        <img
          src={`${API_URL}/api/media/press/hero_helmut_newton_crocodile.jpg`}
          alt="Helmut Newton — Crocodile Eating Ballerina (Pina Bausch, Wuppertal, 1983)"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
          data-testid="news-hero-image"
        />
        {/* Tonal overlays so text remains legible without dulling the print */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

        {/* Photographer credit — bottom-right */}
        <div className="absolute right-6 sm:right-10 bottom-6 sm:bottom-8 text-right text-white/85">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">Photograph</p>
          <p className="font-serif text-base sm:text-lg leading-tight">
            Helmut Newton — Crocodile Eating Ballerina
          </p>
          <p className="text-xs sm:text-sm text-white/60 mt-0.5">
            Wuppertal, for Pina Bausch, 1983
          </p>
        </div>

        {/* Hero copy — bottom-left */}
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-[1400px] mx-auto px-6 pb-16 sm:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-[11px] uppercase tracking-[0.4em] text-white/70 mb-5 font-sans">
                Gallery Archive · Since 1976
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-[1.05]">
                News &amp; Press
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl font-sans leading-relaxed">
                Five decades of exhibitions, acquisitions and headlines from Zebra One Gallery,
                Hampstead — from Hockney to Banksy, Kate Moss to Bowie.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Year + Category Filter */}
      <section className="sticky top-[73px] z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-5 space-y-3">
          {/* Year row */}
          <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
            <span className="text-[11px] uppercase tracking-[3px] text-muted-foreground shrink-0">Year</span>
            <button
              onClick={() => setSelectedYear('all')}
              className={`px-3 py-1.5 text-sm font-sans whitespace-nowrap transition-all ${
                selectedYear === 'all'
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="news-year-all"
            >
              All Years
            </button>
            
            {years.map(({ year, count }) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-3 py-1.5 text-sm font-sans whitespace-nowrap transition-all ${
                  selectedYear === year
                    ? 'text-foreground border-b-2 border-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`news-year-${year}`}
              >
                {year} <span className="text-xs opacity-60">({count})</span>
              </button>
            ))}
          </div>

          {/* Category row */}
          {orderedCategories.length > 1 && (
            <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
              <span className="text-[11px] uppercase tracking-[3px] text-muted-foreground shrink-0">Category</span>
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-sm font-sans whitespace-nowrap transition-all ${
                  selectedCategory === 'all'
                    ? 'text-foreground border-b-2 border-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="news-category-all"
              >
                All
              </button>
              {orderedCategories.map(([cat, count]) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 text-sm font-sans whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? 'text-foreground border-b-2 border-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`news-category-${cat.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  {cat} <span className="text-xs opacity-60">({count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* News Timeline */}
      <section className="py-20 px-6">
        <div className="max-w-[1400px] mx-auto">
          {sortedYears.map((year, yearIndex) => (
            <motion.div
              key={year}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: yearIndex * 0.1 }}
              className="mb-24"
            >
              {/* Year Header */}
              <div className="flex items-center gap-6 mb-12 pb-6 border-b border-border">
                <h2 className="font-serif text-4xl md:text-5xl text-foreground">
                  {year}
                </h2>
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-sm text-muted-foreground font-sans">
                  {newsByYear[year].length} {newsByYear[year].length === 1 ? 'article' : 'articles'}
                </span>
              </div>

              {/* Articles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                {newsByYear[year].map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ 
                      duration: 0.6,
                      delay: index * 0.1,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                    className="group"
                  >
                    {/* Article hero: real image if the article has one,
                        otherwise the typographic fallback (keeps legacy
                        press-archive imports looking editorial rather than
                        broken). */}
                    <motion.div
                      className="relative aspect-[5/3] mb-6 overflow-hidden bg-foreground text-background flex flex-col justify-between p-5 sm:p-6"
                      whileHover={{ scale: 0.99 }}
                      transition={{ duration: 0.4 }}
                      data-testid="news-card-hero"
                    >
                      {article.image_url ? (
                        <Link
                          to={`/news/${article.id}`}
                          className="absolute inset-0 block"
                          aria-label={article.title}
                          data-testid={`news-card-image-link-${article.id}`}
                        >
                          <img
                            src={article.image_url.startsWith('http')
                              ? article.image_url
                              : `${API_URL}${article.image_url}`}
                            alt={article.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          {/* Bottom gradient so the label chips read cleanly */}
                          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
                        </Link>
                      ) : null}

                      <div className="relative z-10 flex items-center justify-between text-[0.6rem] uppercase tracking-[0.25em] opacity-90 font-sans">
                        <span>{article.image_url ? (article.publication || 'Zebra One Gallery') : 'Press Archive'}</span>
                        <span>{article.year}</span>
                      </div>
                      {!article.image_url && (
                        <div className="relative z-10">
                          <p className="font-serif text-xl sm:text-2xl leading-[1.05] line-clamp-3">
                            {article.publication || article.title}
                          </p>
                          {article.publication && (
                            <p className="mt-2.5 text-[0.65rem] uppercase tracking-[0.2em] opacity-60 font-sans">
                              {article.category || 'Editorial'}
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>

                    {/* Category Badge */}
                    {article.category && (
                      <div className="mb-4">
                        <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-foreground text-background font-sans">
                          {article.category}
                        </span>
                      </div>
                    )}

                    {/* Date */}
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 font-sans">
                      {article.date}
                    </p>

                    {/* Title — links to dedicated article page */}
                    <h3 className="font-serif text-2xl text-foreground mb-4 leading-tight">
                      <Link
                        to={`/news/${article.id}`}
                        className="group-hover:text-muted-foreground transition-colors duration-300 hover:underline underline-offset-4"
                        data-testid={`news-card-title-${article.id}`}
                      >
                        {article.title}
                      </Link>
                    </h3>

                    {/* Excerpt — strip HTML tags + markdown syntax and take
                        the first ~280 chars so cards show clean editorial
                        copy instead of leaking raw <figure>/<img> markup. */}
                    <div className="text-muted-foreground font-sans leading-relaxed mb-6 line-clamp-4">
                      {(article.content || '')
                        .replace(/<[^>]+>/g, ' ')      // strip HTML tags
                        .replace(/[#*_>`~]/g, '')       // strip markdown syntax
                        .replace(/\s+/g, ' ')            // collapse whitespace
                        .trim()
                        .slice(0, 320)}
                      {(article.content || '').length > 320 ? '…' : ''}
                    </div>

                    {/* Publication */}
                    {article.publication && (
                      <p className="text-xs text-muted-foreground/60 mb-6 font-sans">
                        {article.publication}
                      </p>
                    )}

                    {/* Read More — always shows; opens dedicated article page */}
                    <div className="flex flex-wrap items-center gap-5">
                      <Link
                        to={`/news/${article.id}`}
                        className="inline-block"
                        data-testid={`news-card-readmore-${article.id}`}
                      >
                        <motion.div
                          className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-foreground font-sans border-b border-foreground pb-1 group-hover:gap-4 transition-all duration-300"
                          whileHover={{ x: 4 }}
                        >
                          Read full story
                          <ChevronDown className="w-4 h-4 -rotate-90 transition-transform group-hover:translate-x-1" />
                        </motion.div>
                      </Link>
                      {article.url && (
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`news-card-source-${article.id}`}
                        >
                          Original source ↗
                        </a>
                      )}
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Empty State */}
          {sortedYears.length === 0 && (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-muted-foreground/20 mx-auto mb-6" />
              <p className="text-muted-foreground font-sans">
                No news articles found for this selection.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Archive Footer */}
      <section className="py-20 px-6 border-t border-border bg-muted/20">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4 font-sans">
              Since 1976
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Five Decades of Contemporary Art
            </h2>
            <p className="text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
              For nearly fifty years, Zebra One Gallery has been at the forefront of the contemporary 
              art scene in Hampstead, presenting exceptional works by established masters and emerging talents.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;
